import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { ChartWrapper } from '../ChartWrapper';
import { ChartEmpty } from './ChartEmpty';

interface IncomingByMonthChartsProps {
  months: Array<{ month: string; value: number; lines: number }>;
  onChartClick: (chartData: unknown) => void;
}

/**
 * Cost tab, Future view: open PO value and line counts for the next twelve
 * months by latest supplier promise date. Months with nothing due show an
 * empty state rather than blank axes (everything open may already be overdue).
 */
export function IncomingByMonthCharts({ months, onChartClick }: IncomingByMonthChartsProps) {
  const { formatCurrency, formatCompactCurrency } = useFormatters();
  const lines = months.map((m) => ({ month: m.month, lines: m.lines ?? 0 }));
  const nothingDue = months.every((m) => !m.value && !m.lines);
  const empty = (testId: string) => (
    <ChartEmpty height={200} testId={testId}
                message="No open PO lines are due in the next twelve months. Anything still open is already in arrears." />
  );
  return (
    <div className="grid grid-cols-2 gap-6">
      <ChartWrapper title="Incoming by Month (Value)" data={months} exportFilename="incoming-by-month-value">
        {nothingDue ? empty('cost-incoming-value-empty') : (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={months} onClick={onChartClick} style={{ cursor: 'pointer' }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(value: number) => formatCompactCurrency(value)} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} formatter={(value: number) => formatCurrency(value)} />
              <Bar dataKey="value" fill="#3b82f6" name="Value" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartWrapper>
      <ChartWrapper title="Incoming by Month (PO Lines)" data={lines} exportFilename="incoming-by-month-lines">
        {nothingDue ? empty('cost-incoming-lines-empty') : (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={lines} onClick={onChartClick} style={{ cursor: 'pointer' }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }} />
              <Bar dataKey="lines" fill="#10b981" name="PO Lines" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartWrapper>
    </div>
  );
}
